import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import { Logo } from "@/components/Logo";
import { tl, itemPairs, itemAmount, orderAmount, orderPairs, orderTotals } from "@/lib/fair";

const cell = "border border-black px-2 py-1.5";

export default function FairOrderPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/fair/orders"), api.get("/fair/products"), api.get("/fair/customers")]).then(([o, p, c]) => {
      const order = o.data.find((x) => x.id === id);
      if (!order) { setNotFound(true); return; }
      setData({ order, products: p.data, customer: c.data.find((x) => x.id === order.customer_id) });
    }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="p-10 text-[#EF4444]" data-testid="fair-print-not-found">Fuar siparişi bulunamadı.</div>;
  if (!data) return <div className="p-10 text-[#6B7280]">Yükleniyor...</div>;
  const { order, products, customer } = data;
  const pr = (pid) => products.find((p) => p.id === pid);
  const items = [...order.items].sort((a, b) => `${pr(a.fair_product_id)?.model_code}`.localeCompare(`${pr(b.fair_product_id)?.model_code}`));
  const total = items.reduce((a, b) => a + b.koli, 0);
  const tt = orderTotals(order);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="fair-print-page">
      <div className="max-w-[800px] mx-auto p-8 sm:p-12">
        <div className="flex justify-end mb-6 print:hidden">
          <button onClick={() => window.print()} data-testid="fair-print-btn"
            className="flex items-center gap-2 bg-[#D97706] hover:bg-[#B45309] text-white px-5 h-11 rounded-md font-display uppercase tracking-wide">
            <Printer className="w-4 h-4" /> Yazdır
          </button>
        </div>
        <div className="flex justify-between items-start border-b-2 border-black pb-4 mb-6">
          <div>
            <Logo dark className="text-4xl" subtitle="Ayakkabı Üretim" testId="print-logo" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-4" data-testid="print-title">Fuar Sipariş Fişi</h1>
          </div>
          <div className="text-right text-sm font-mono">
            <p>No: <b data-testid="fair-print-no">{order.id.slice(0, 8).toUpperCase()}</b></p>
            <p>Tarih: {new Date(order.created_at).toLocaleDateString("tr-TR")}</p>
          </div>
        </div>
        <div className="mb-6 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Müşteri</p>
          <p className="text-lg font-semibold" data-testid="fair-print-customer">{order.customer_name}</p>
          {customer?.contact_name && <p>Yetkili: {customer.contact_name}</p>}
          {customer?.phone && <p>Tel: {customer.phone}</p>}
          {customer?.address && <p>{customer.address}</p>}
        </div>
        <table className="w-full text-sm border border-black" data-testid="fair-print-table">
          <thead className="bg-gray-100">
            <tr className="text-left">
              <th className={cell}>#</th><th className={cell}>Model Kodu</th><th className={cell}>Renk</th>
              <th className={`${cell} text-right`}>Koli</th>
              <th className={`${cell} text-right`}>Koli İçi</th>
              <th className={`${cell} text-right`}>Toplam Çift</th>
              <th className={`${cell} text-right`}>Çift Fiyatı</th>
              <th className={`${cell} text-right`}>Tutar</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.fair_product_id}>
                <td className={cell}>{i + 1}</td>
                <td className={`${cell} font-mono`}>{pr(it.fair_product_id)?.model_code || "—"}</td>
                <td className={cell}>{pr(it.fair_product_id)?.model_color || "—"}</td>
                <td className={`${cell} text-right font-mono font-bold`}>{it.koli}</td>
                <td className={`${cell} text-right font-mono`}>{it.pairs || 8}</td>
                <td className={`${cell} text-right font-mono`}>{itemPairs(it)}</td>
                <td className={`${cell} text-right font-mono`}>{tl(it.price)}</td>
                <td className={`${cell} text-right font-mono font-bold`}>{tl(itemAmount(it))}</td>
              </tr>
            ))}
            <tr className="font-bold">
              <td colSpan={3} className={`${cell} text-right`}>TOPLAM</td>
              <td className={`${cell} text-right font-mono`} data-testid="fair-print-total">{total}</td>
              <td className={cell}></td>
              <td className={`${cell} text-right font-mono`}>{orderPairs(order)}</td>
              <td className={cell}></td>
              <td className={`${cell} text-right font-mono`} data-testid="fair-print-amount">{tl(orderAmount(order))}</td>
            </tr>
          </tbody>
        </table>
        <div className="flex justify-end mt-4" data-testid="fair-print-totals">
          <table className="text-sm min-w-[300px]">
            <tbody>
              {[["Ara Toplam", tl(tt.subtotal), "subtotal"], [`İskonto (%${order.discount_pct || 0})`, `− ${tl(tt.discount)}`, "discount"],
                ["İskontolu Toplam", tl(tt.net), "net"], [`KDV (%${order.vat_pct ?? 10})`, tl(tt.vat), "vat"]].map(([l, v, k]) => (
                <tr key={k}><td className="py-1 pr-6">{l}</td><td className="py-1 text-right font-mono" data-testid={`fair-print-${k}`}>{v}</td></tr>
              ))}
              <tr className="border-t-2 border-black font-bold text-base">
                <td className="py-1.5 pr-6">GENEL TOPLAM</td><td className="py-1.5 text-right font-mono" data-testid="fair-print-grand">{tl(tt.grand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {order.note && <p className="text-sm mt-4">Not: {order.note}</p>}
        <p className="text-xs text-gray-600 mt-4">Bu fiş fuarda alınan ön sipariş talebidir; kesin sipariş ayrıca onaylanır.</p>
        <div className="grid grid-cols-2 gap-10 mt-16 text-sm text-center">
          <div className="border-t border-black pt-2">CARRANO</div>
          <div className="border-t border-black pt-2">Müşteri</div>
        </div>
      </div>
    </div>
  );
}
